import React, { useState } from 'react';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Subtitle1Bold } from '../../lib/font';
import dayjs from 'dayjs';
import {
    usePauseSubscription,
    useResumeSubscription,
    useCancelPause,
    useUpdatePausePeriod,
} from '@/features/facilitySubscription/hooks';
import { IFacilitySubscription, PausePeriod, PausePeriodStatus } from '@/features/facilitySubscription/types';

const getMutationErrorMessage = (error: any, fallback: string) =>
    error?.serverMessage || error?.message || fallback;

const inputClass =
    'border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px] w-full focus:outline-none focus:border-blue-400 transition-colors';

const btnBase: React.CSSProperties = {
    height: 38,
    padding: '0 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'all 0.15s',
};
const btnStyles = {
    primary: { ...btnBase, backgroundColor: '#3B82F6', color: 'white' },
    neutral: { ...btnBase, backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB' },
    pause: { ...btnBase, backgroundColor: 'white', color: '#475569', border: '1px solid #CBD5E1' },
    danger: { ...btnBase, backgroundColor: 'white', color: '#EF4444', border: '1px solid #FCA5A5' },
};

const PERIOD_STATUS_CFG: Record<PausePeriodStatus, { label: string; bg: string; color: string }> = {
    scheduled: { label: '정지 예정', bg: '#F1F5F9', color: '#64748B' },
    active: { label: '정지 중', bg: '#EEF2F6', color: '#475569' },
    resumed: { label: '재개 완료', bg: '#ECFDF5', color: '#059669' },
    cancelled: { label: '취소됨', bg: '#FEF2F2', color: '#DC2626' },
};

const fmtDate = (v?: string | null) => (v ? dayjs(v).format('YYYY.MM.DD') : '-');

interface Props {
    centerId: string;
    memberId: string;
    subscription: IFacilitySubscription;
    createAlert: any;
    /** 정지/재개/취소 성공 후 (보통 부모 모달 닫기) */
    onChanged: () => void;
}

const SubscriptionPauseManager = ({ centerId, memberId, subscription, createAlert, onChanged }: Props) => {
    const pauseMutation = usePauseSubscription();
    const resumeMutation = useResumeSubscription();
    const cancelMutation = useCancelPause();
    const updatePeriodMutation = useUpdatePausePeriod();

    const summary = subscription.pauseSummary;
    const periods: PausePeriod[] = subscription.pausePeriods ?? [];
    const isPaused = Boolean(summary?.isPaused);
    const scheduled = summary?.scheduledPause ?? null;
    const currentPeriodId = summary?.currentPausePeriodId ?? null;

    // 정지 가능 여부: active 상태 + 진행/예약 정지가 없을 때
    const canPause = subscription.status === 'active' && !isPaused && !scheduled;

    const [showPause, setShowPause] = useState(false);
    const [showResume, setShowResume] = useState(false);
    const [showCancel, setShowCancel] = useState<{ periodId: string } | null>(null);
    const [showHistory, setShowHistory] = useState(false);

    // 정지 폼
    const [pauseStart, setPauseStart] = useState(dayjs().format('YYYY-MM-DD'));
    const [pauseExpectedResume, setPauseExpectedResume] = useState('');
    const [pauseAutoResume, setPauseAutoResume] = useState('');
    const [pauseReason, setPauseReason] = useState('');
    const [pauseMemo, setPauseMemo] = useState('');

    // 재개 폼
    const [resumeDate, setResumeDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [appliedDays, setAppliedDays] = useState('');

    // 취소 폼
    const [cancelReason, setCancelReason] = useState('');

    // 정지 이력 수정 폼
    const [editPeriod, setEditPeriod] = useState<PausePeriod | null>(null);
    const [editStart, setEditStart] = useState('');
    const [editExpectedResume, setEditExpectedResume] = useState('');
    const [editAutoResume, setEditAutoResume] = useState('');
    const [editResumedAt, setEditResumedAt] = useState('');
    const [editAppliedDays, setEditAppliedDays] = useState('');
    const [editReason, setEditReason] = useState('');
    const [editMemo, setEditMemo] = useState('');

    const toInput = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '');

    const openEdit = (p: PausePeriod) => {
        setEditPeriod(p);
        setEditStart(toInput(p.startedAt));
        setEditExpectedResume(toInput(p.expectedResumeDate));
        setEditAutoResume(toInput(p.autoResumeAt));
        setEditResumedAt(toInput(p.resumedAt));
        setEditAppliedDays(p.status === 'resumed' ? String(p.appliedExtensionDays ?? 0) : '');
        setEditReason(p.reason || '');
        setEditMemo(p.memo || '');
    };

    const handleEditSave = async () => {
        if (!editPeriod) return;
        const isResumed = editPeriod.status === 'resumed';
        const body: Record<string, unknown> = {
            // hasOwnProperty 기반 부분 수정이므로 값/ null 을 명시 전송
            startedAt: editStart || null,
            reason: editReason,
            memo: editMemo,
        };
        if (isResumed) {
            body.resumedAt = editResumedAt || null;
            if (editAppliedDays !== '') body.appliedExtensionDays = Number(editAppliedDays);
        } else {
            body.expectedResumeDate = editExpectedResume || null;
            body.autoResumeAt = editAutoResume || null;
        }
        try {
            await updatePeriodMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                memberId,
                periodId: editPeriod._id,
                body,
            });
            setEditPeriod(null);
            onChanged();
            createAlert('정지 이력 수정 완료', '정지 이력이 수정되었습니다.', false);
        } catch (error) {
            createAlert('수정 실패', getMutationErrorMessage(error, '정지 이력 수정에 실패했습니다.'), true);
        }
    };

    const openPause = () => {
        setPauseStart(dayjs().format('YYYY-MM-DD'));
        setPauseExpectedResume('');
        setPauseAutoResume('');
        setPauseReason('');
        setPauseMemo('');
        setShowPause(true);
    };

    const openResume = () => {
        setResumeDate(dayjs().format('YYYY-MM-DD'));
        setAppliedDays('');
        setShowResume(true);
    };

    const isFutureStart = pauseStart && dayjs(pauseStart).startOf('day').isAfter(dayjs().startOf('day'));
    const pauseDateError =
        pauseExpectedResume && pauseStart && pauseExpectedResume < pauseStart
            ? '예상 재개일은 정지 시작일보다 빠를 수 없습니다.'
            : pauseAutoResume && pauseStart && pauseAutoResume < pauseStart
            ? '자동 재개일은 정지 시작일보다 빠를 수 없습니다.'
            : null;

    // 재개 시 자동 계산 연장일 (정지 시작일 ~ 재개일)
    const resumeStart = summary?.currentStartedAt ? dayjs(summary.currentStartedAt) : null;
    const autoCalcDays = resumeStart ? Math.max(0, dayjs(resumeDate).diff(resumeStart, 'day')) : null;
    const effectiveAppliedDays = appliedDays !== '' ? Number(appliedDays) : autoCalcDays ?? 0;

    const handlePause = async () => {
        if (!pauseStart) {
            createAlert('정지 실패', '정지 시작일을 입력해주세요.', true);
            return;
        }
        if (pauseDateError) return;
        try {
            const res = await pauseMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                memberId,
                body: {
                    startedAt: pauseStart,
                    expectedResumeDate: pauseExpectedResume || undefined,
                    autoResumeAt: pauseAutoResume || undefined,
                    reason: pauseReason.trim() || undefined,
                    memo: pauseMemo.trim() || undefined,
                },
            });
            setShowPause(false);
            onChanged();
            createAlert(
                res?.data?.scheduled ? '정지 예약 완료' : '일시정지 완료',
                res?.data?.scheduled
                    ? `${fmtDate(pauseStart)}부터 정지 예약되었습니다.`
                    : '이용권이 일시정지되었습니다.',
                false,
            );
        } catch (error) {
            createAlert('정지 실패', getMutationErrorMessage(error, '일시정지에 실패했습니다.'), true);
        }
    };

    const handleResume = async () => {
        try {
            const res = await resumeMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                memberId,
                body: {
                    resumedAt: resumeDate,
                    appliedExtensionDays: appliedDays !== '' ? Number(appliedDays) : undefined,
                },
            });
            setShowResume(false);
            onChanged();
            const applied = res?.data?.resumePreview?.appliedExtensionDays ?? effectiveAppliedDays;
            createAlert('재활성화 완료', `종료일이 ${applied}일 연장되었습니다.`, false);
        } catch (error) {
            createAlert('재활성화 실패', getMutationErrorMessage(error, '재활성화에 실패했습니다.'), true);
        }
    };

    const handleCancel = async () => {
        if (!showCancel) return;
        try {
            await cancelMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                memberId,
                periodId: showCancel.periodId,
                body: { reason: cancelReason.trim() || undefined },
            });
            setShowCancel(null);
            setCancelReason('');
            onChanged();
            createAlert('정지 취소 완료', '정지가 연장 없이 취소되었습니다.', false);
        } catch (error) {
            createAlert('정지 취소 실패', getMutationErrorMessage(error, '정지 취소에 실패했습니다.'), true);
        }
    };

    return (
        <Col className="gap-[12px] p-[16px] bg-[#F8FAFC] rounded-[10px] border border-solid border-[#E2E8F0]">
            <Row className="items-center justify-between">
                <Subtitle1Bold color={colors.LAVEL_1} style={{ fontSize: 14 }}>
                    일시정지 관리
                </Subtitle1Bold>
                {periods.length > 0 && (
                    <button
                        onClick={() => setShowHistory((v) => !v)}
                        style={{
                            fontSize: 12,
                            color: '#64748B',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }}
                    >
                        정지 이력 {periods.length}건 {showHistory ? '접기' : '보기'}
                    </button>
                )}
            </Row>

            {/* 현재 상태 */}
            {isPaused && (
                <Col style={{ gap: 2, fontSize: 13, color: '#475569' }}>
                    <span>
                        <b style={{ color: '#475569' }}>정지 중</b> · {fmtDate(summary?.currentStartedAt)} 시작
                        {summary?.currentPausedDays ? ` (${summary.currentPausedDays}일 경과)` : ''}
                    </span>
                    {summary?.currentAutoResumeAt && (
                        <span style={{ fontSize: 12, color: '#64748B' }}>
                            {fmtDate(summary.currentAutoResumeAt)} 자동 재개 예정
                        </span>
                    )}
                    {summary?.currentReason && (
                        <span style={{ fontSize: 12, color: '#64748B' }}>사유: {summary.currentReason}</span>
                    )}
                </Col>
            )}
            {!isPaused && scheduled && (
                <Col style={{ gap: 2, fontSize: 13, color: '#64748B' }}>
                    <span>
                        <b>정지 예정</b> · {fmtDate(scheduled.startedAt)}부터
                    </span>
                    {scheduled.autoResumeAt && (
                        <span style={{ fontSize: 12 }}>{fmtDate(scheduled.autoResumeAt)} 자동 재개 예정</span>
                    )}
                </Col>
            )}
            {!isPaused && !scheduled && subscription.status === 'active' && (
                <span style={{ fontSize: 13, color: '#64748B' }}>현재 정지 이력이 없습니다.</span>
            )}
            {subscription.status !== 'active' && (
                <span style={{ fontSize: 13, color: '#94A3B8' }}>
                    {subscription.status === 'refunded' ? '환불된' : '만료된'} 이용권은 정지할 수 없습니다.
                </span>
            )}

            {/* 액션 버튼 */}
            <Row style={{ gap: 8 }}>
                {canPause && (
                    <button onClick={openPause} style={btnStyles.pause}>
                        일시정지
                    </button>
                )}
                {isPaused && (
                    <>
                        <button onClick={openResume} style={btnStyles.primary}>
                            재활성화
                        </button>
                        {currentPeriodId && (
                            <button onClick={() => setShowCancel({ periodId: currentPeriodId })} style={btnStyles.danger}>
                                정지 취소
                            </button>
                        )}
                    </>
                )}
                {!isPaused && scheduled?.periodId && (
                    <button onClick={() => setShowCancel({ periodId: scheduled.periodId! })} style={btnStyles.danger}>
                        예약 취소
                    </button>
                )}
            </Row>

            {/* 정지 이력 타임라인 */}
            {showHistory && periods.length > 0 && (
                <Col style={{ gap: 8, marginTop: 4 }}>
                    {[...periods]
                        .sort(
                            (a, b) =>
                                new Date(b.startedAt || b.createdAt || 0).getTime() -
                                new Date(a.startedAt || a.createdAt || 0).getTime(),
                        )
                        .map((p) => {
                            const cfg = PERIOD_STATUS_CFG[p.status] || PERIOD_STATUS_CFG.active;
                            const actor =
                                p.cancelledBy?.name || p.resumedBy?.name || p.createdBy?.name || '';
                            return (
                                <Col
                                    key={p._id}
                                    style={{
                                        gap: 4,
                                        padding: '10px 12px',
                                        backgroundColor: '#fff',
                                        borderRadius: 8,
                                        border: '1px solid #E2E8F0',
                                    }}
                                >
                                    <Row style={{ alignItems: 'center', gap: 8 }}>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                padding: '2px 8px',
                                                borderRadius: 6,
                                                backgroundColor: cfg.bg,
                                                color: cfg.color,
                                            }}
                                        >
                                            {cfg.label}
                                        </span>
                                        <span style={{ fontSize: 12, color: '#475569' }}>
                                            {fmtDate(p.startedAt)}
                                            {p.resumedAt ? ` ~ ${fmtDate(p.resumedAt)}` : ' ~'}
                                        </span>
                                        {p.status !== 'cancelled' && (
                                            <button
                                                onClick={() => openEdit(p)}
                                                style={{
                                                    marginLeft: 'auto',
                                                    fontSize: 11,
                                                    color: '#3B82F6',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                }}
                                            >
                                                수정
                                            </button>
                                        )}
                                    </Row>
                                    <Row style={{ gap: 12, fontSize: 11, color: '#94A3B8', flexWrap: 'wrap' }}>
                                        {p.status === 'resumed' && (
                                            <span>적용 연장 {p.appliedExtensionDays}일</span>
                                        )}
                                        {p.autoResumeAt && p.status !== 'resumed' && (
                                            <span>자동재개 {fmtDate(p.autoResumeAt)}</span>
                                        )}
                                        {p.reason && <span>사유: {p.reason}</span>}
                                        {actor && <span>처리: {actor}</span>}
                                        {p.source === 'import' && <span>가져오기</span>}
                                    </Row>
                                </Col>
                            );
                        })}
                </Col>
            )}

            {/* 정지 모달 */}
            {showPause && (
                <Overlay onClose={() => setShowPause(false)}>
                    <Subtitle1Bold>일시정지</Subtitle1Bold>
                    <Col style={{ gap: 14, marginTop: 16 }}>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>정지 시작일</Body2Regular>
                            <input
                                className={inputClass}
                                type="date"
                                value={pauseStart}
                                onChange={(e) => setPauseStart(e.target.value)}
                            />
                            {isFutureStart && (
                                <span style={{ fontSize: 12, color: '#2563EB' }}>
                                    미래 날짜 → 해당일에 자동으로 정지가 시작되는 예약 정지로 등록됩니다.
                                </span>
                            )}
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>예상 재개일 (선택, 표시용)</Body2Regular>
                            <input
                                className={inputClass}
                                type="date"
                                value={pauseExpectedResume}
                                onChange={(e) => setPauseExpectedResume(e.target.value)}
                            />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>자동 재개일 (선택)</Body2Regular>
                            <input
                                className={inputClass}
                                type="date"
                                value={pauseAutoResume}
                                onChange={(e) => setPauseAutoResume(e.target.value)}
                            />
                            <span style={{ fontSize: 12, color: '#64748B' }}>
                                입력 시 해당일에 자동 재개되고 정지 기간만큼 종료일이 연장됩니다.
                            </span>
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>사유 (선택)</Body2Regular>
                            <input
                                className={inputClass}
                                value={pauseReason}
                                onChange={(e) => setPauseReason(e.target.value)}
                                placeholder="예: 개인 사정"
                            />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>메모 (선택)</Body2Regular>
                            <textarea
                                className={`${inputClass} resize-none`}
                                rows={2}
                                value={pauseMemo}
                                onChange={(e) => setPauseMemo(e.target.value)}
                            />
                        </Col>
                        {pauseDateError && <span style={{ fontSize: 12, color: '#EF4444' }}>{pauseDateError}</span>}
                        <span style={{ fontSize: 12, color: '#64748B' }}>
                            재활성화 시 정지 기간만큼 종료일이 연장됩니다.
                        </span>
                    </Col>
                    <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <button onClick={() => setShowPause(false)} style={btnStyles.neutral}>
                            취소
                        </button>
                        <button
                            onClick={handlePause}
                            disabled={pauseMutation.isLoading || !!pauseDateError}
                            style={{
                                ...btnStyles.primary,
                                opacity: pauseMutation.isLoading || pauseDateError ? 0.5 : 1,
                                cursor: pauseMutation.isLoading || pauseDateError ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isFutureStart ? '정지 예약' : '정지'}
                        </button>
                    </Row>
                </Overlay>
            )}

            {/* 재개 모달 */}
            {showResume && (
                <Overlay onClose={() => setShowResume(false)}>
                    <Subtitle1Bold>재활성화</Subtitle1Bold>
                    <Col style={{ gap: 14, marginTop: 16 }}>
                        <Col
                            style={{ gap: 4, padding: '12px 14px', backgroundColor: '#F8FAFC', borderRadius: 8 }}
                        >
                            <span style={{ fontSize: 12, color: '#64748B' }}>
                                정지 시작일: <b style={{ color: '#1F2937' }}>{fmtDate(summary?.currentStartedAt)}</b>
                            </span>
                            <span style={{ fontSize: 12, color: '#64748B' }}>
                                현재 종료일: <b style={{ color: '#1F2937' }}>{fmtDate(subscription.endDate)}</b>
                            </span>
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>재개일</Body2Regular>
                            <input
                                className={inputClass}
                                type="date"
                                value={resumeDate}
                                onChange={(e) => setResumeDate(e.target.value)}
                            />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>적용 연장일</Body2Regular>
                            <input
                                className={inputClass}
                                type="number"
                                min={0}
                                value={appliedDays}
                                onChange={(e) => setAppliedDays(e.target.value)}
                                placeholder={autoCalcDays != null ? `자동 계산: ${autoCalcDays}일` : '연장일'}
                            />
                            {autoCalcDays != null && (
                                <span style={{ fontSize: 12, color: '#64748B' }}>
                                    자동 계산 연장일: <b>{autoCalcDays}일</b> (비워두면 자동 적용)
                                </span>
                            )}
                            <span style={{ fontSize: 12, color: '#2563EB' }}>
                                종료일 → {dayjs(subscription.endDate).add(effectiveAppliedDays, 'day').format('YYYY.MM.DD')} ({effectiveAppliedDays}일 연장)
                            </span>
                        </Col>
                    </Col>
                    <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <button onClick={() => setShowResume(false)} style={btnStyles.neutral}>
                            취소
                        </button>
                        <button
                            onClick={handleResume}
                            disabled={resumeMutation.isLoading}
                            style={{
                                ...btnStyles.primary,
                                opacity: resumeMutation.isLoading ? 0.5 : 1,
                                cursor: resumeMutation.isLoading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            재활성화
                        </button>
                    </Row>
                </Overlay>
            )}

            {/* 정지 취소 확인 */}
            {showCancel && (
                <Overlay onClose={() => setShowCancel(null)}>
                    <Subtitle1Bold>정지 취소</Subtitle1Bold>
                    <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 8 }}>
                        이 정지를 <b>연장 없이</b> 취소합니다. 이력에는 '취소됨'으로 남습니다.
                    </span>
                    <Col style={{ gap: 6, marginTop: 12 }}>
                        <Body2Regular color={colors.LAVEL_2}>취소 사유 (선택)</Body2Regular>
                        <input
                            className={inputClass}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="예: 오등록"
                        />
                    </Col>
                    <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <button onClick={() => setShowCancel(null)} style={btnStyles.neutral}>
                            닫기
                        </button>
                        <button
                            onClick={handleCancel}
                            disabled={cancelMutation.isLoading}
                            style={{
                                ...btnStyles.primary,
                                backgroundColor: '#EF4444',
                                opacity: cancelMutation.isLoading ? 0.5 : 1,
                                cursor: cancelMutation.isLoading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            정지 취소
                        </button>
                    </Row>
                </Overlay>
            )}

            {/* 정지 이력 수정 모달 */}
            {editPeriod && (
                <Overlay onClose={() => setEditPeriod(null)}>
                    <Subtitle1Bold>정지 이력 수정</Subtitle1Bold>
                    <span style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
                        {PERIOD_STATUS_CFG[editPeriod.status]?.label} 이력의 정보를 정정합니다.
                    </span>
                    <Col style={{ gap: 14, marginTop: 16 }}>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>정지 시작일</Body2Regular>
                            <input
                                className={inputClass}
                                type="date"
                                value={editStart}
                                onChange={(e) => setEditStart(e.target.value)}
                            />
                        </Col>
                        {editPeriod.status === 'resumed' ? (
                            <>
                                <Col style={{ gap: 6 }}>
                                    <Body2Regular color={colors.LAVEL_2}>재개일</Body2Regular>
                                    <input
                                        className={inputClass}
                                        type="date"
                                        value={editResumedAt}
                                        onChange={(e) => setEditResumedAt(e.target.value)}
                                    />
                                </Col>
                                <Col style={{ gap: 6 }}>
                                    <Body2Regular color={colors.LAVEL_2}>적용 연장일</Body2Regular>
                                    <input
                                        className={inputClass}
                                        type="number"
                                        min={0}
                                        value={editAppliedDays}
                                        onChange={(e) => setEditAppliedDays(e.target.value)}
                                        placeholder="비우면 시작일~재개일 자동 계산"
                                    />
                                    <span style={{ fontSize: 12, color: '#64748B' }}>
                                        기존 적용일과의 차이만큼 종료일이 보정됩니다.
                                    </span>
                                </Col>
                            </>
                        ) : (
                            <>
                                <Col style={{ gap: 6 }}>
                                    <Body2Regular color={colors.LAVEL_2}>예상 재개일 (선택)</Body2Regular>
                                    <input
                                        className={inputClass}
                                        type="date"
                                        value={editExpectedResume}
                                        onChange={(e) => setEditExpectedResume(e.target.value)}
                                    />
                                </Col>
                                <Col style={{ gap: 6 }}>
                                    <Body2Regular color={colors.LAVEL_2}>자동 재개일 (선택)</Body2Regular>
                                    <input
                                        className={inputClass}
                                        type="date"
                                        value={editAutoResume}
                                        onChange={(e) => setEditAutoResume(e.target.value)}
                                    />
                                </Col>
                            </>
                        )}
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>사유</Body2Regular>
                            <input
                                className={inputClass}
                                value={editReason}
                                onChange={(e) => setEditReason(e.target.value)}
                            />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>메모</Body2Regular>
                            <textarea
                                className={`${inputClass} resize-none`}
                                rows={2}
                                value={editMemo}
                                onChange={(e) => setEditMemo(e.target.value)}
                            />
                        </Col>
                    </Col>
                    <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <button onClick={() => setEditPeriod(null)} style={btnStyles.neutral}>
                            취소
                        </button>
                        <button
                            onClick={handleEditSave}
                            disabled={updatePeriodMutation.isLoading}
                            style={{
                                ...btnStyles.primary,
                                opacity: updatePeriodMutation.isLoading ? 0.5 : 1,
                                cursor: updatePeriodMutation.isLoading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            저장
                        </button>
                    </Row>
                </Overlay>
            )}
        </Col>
    );
};

// 정지 관련 서브 모달 공통 오버레이
const Overlay = ({ children, onClose }: { children: React.ReactNode; onClose: () => void }) => (
    <div
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
        }}
        onClick={onClose}
    >
        <Col
            style={{
                width: 420,
                maxHeight: '85vh',
                overflowY: 'auto',
                backgroundColor: '#fff',
                borderRadius: 14,
                padding: 24,
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
            {children}
        </Col>
    </div>
);

export default SubscriptionPauseManager;
